import { computed, ref } from 'vue';

export const RECORDING_STATE = {
  RECORDING: 'recording',
  PLAYING: 'playing',
  PAUSED: 'paused',
  STOPPED: 'stopped',
};

/**
 * Composable for Reply Actions logic (Send/Record button behavior)
 * @param {Object} options - Configuration options
 * @param {import('vue').Ref<string>} options.message - Message content ref
 * @param {import('vue').Ref<boolean>} options.hasAttachments - Has attachments flag
 * @param {import('vue').Ref<boolean>} options.isRecordingAudio - Recording state flag
 * @param {import('vue').Ref<boolean>} options.isNote - Private note mode flag
 * @param {import('vue').Ref<boolean>} options.isAudioRecorderEnabled - Audio feature enabled
 * @param {import('vue').Ref<string>} options.recordingAudioState - Current recording state
 * @param {import('vue').Ref<boolean>} options.isSendDisabled - Send disabled flag
 * @param {Function} options.onSend - Send message callback
 * @param {Function} options.toggleAudioRecorder - Toggle recorder callback
 * @param {Function} options.onSendAudio - Send audio callback
 */
export function useReplyActions({
  message,
  hasAttachments,
  isRecordingAudio,
  isNote,
  isAudioRecorderEnabled,
  isSendDisabled,
  onSend,
  toggleAudioRecorder,
  onSendAudio,
}) {
  const isSendingAudio = ref(false);

  // Show Mic button when input is empty
  const showRecordButton = computed(() => {
    if (!isAudioRecorderEnabled.value) return false;
    const isMessageEmpty = !message.value || message.value.trim().length === 0;
    return isMessageEmpty && !hasAttachments.value && !isRecordingAudio.value;
  });

  // Button Icon
  const sendButtonIcon = computed(() => {
    if (showRecordButton.value) return 'i-ph-microphone';
    return 'i-ph-paper-plane-right';
  });

  // Button Color
  const sendButtonColor = computed(() => {
    if (isRecordingAudio.value) return 'teal';
    if (showRecordButton.value) return 'slate';
    return isNote.value ? 'amber' : 'blue';
  });

  // Action Strategy Pattern
  const getActionType = () => {
    if (isRecordingAudio.value) return 'sendAudio';
    if (showRecordButton.value) return 'startRecord';
    if (!isSendDisabled.value) return 'send';
    return 'disabled';
  };

  const actionHandlers = {
    sendAudio: () => {
      isSendingAudio.value = true;
      onSendAudio?.();
    },
    startRecord: () => {
      toggleAudioRecorder?.();
    },
    send: () => {
      onSend?.();
    },
    disabled: () => {
      // Do nothing
    },
  };

  const handleMainButtonClick = () => {
    const actionType = getActionType();
    actionHandlers[actionType]();
  };

  const handleFinishRecording = (file, emitSendAudio, emitFinishRecord) => {
    if (isSendingAudio.value) {
      emitSendAudio(file);
      isSendingAudio.value = false;
    } else {
      emitFinishRecord(file);
    }
  };

  return {
    showRecordButton,
    sendButtonIcon,
    sendButtonColor,
    isSendingAudio,
    handleMainButtonClick,
    handleFinishRecording,
    RECORDING_STATE,
  };
}
